import {
  matchOrganization,
  normalizeOrganizationCode,
  normalizeOrganizationName,
  type OrganizationCandidate,
  type OrganizationMatch,
} from "./member-organization";

/**
 * Working out which organization each row of a roster belongs to.
 *
 * Hand entry and corrections choose an organization from a list. The roster import cannot:
 * its school column carries whatever WVCEA's registration system holds, as text.
 *
 * The thing that makes this bearable is that a file of four hundred teachers contains
 * perhaps twenty distinct schools. So the file is grouped by the school it names and each
 * distinct school is matched once. An organizer confirms a short list rather than reading
 * four hundred rows, and a school that needs correcting is corrected in one place instead
 * of forty.
 */
export interface SuppliedSchool {
  /** Stable across a preview and the import that follows, so an answer can be sent back. */
  key: string;
  /** As the file wrote it, for showing to somebody who has the file open. */
  name: string | null;
  code: string | null;
  rowCount: number;
}

export interface SchoolMatchResult extends SuppliedSchool {
  match: OrganizationMatch;
}

/**
 * The distinct schools a file names, in the order they first appear, with how many rows
 * each covers. Rows naming no school at all are gathered under one entry, because "these
 * forty have no school" is a thing an organizer needs to see rather than forty silences.
 */
export function summariseSuppliedSchools(
  rows: readonly { schoolName: string | null; schoolIdentifier: string | null }[],
): readonly SuppliedSchool[] {
  const byKey = new Map<string, { name: string | null; code: string | null; rowCount: number }>();
  for (const row of rows) {
    const key = suppliedSchoolKey(row.schoolName, row.schoolIdentifier);
    const existing = byKey.get(key);
    if (existing) {
      existing.rowCount += 1;
      continue;
    }
    byKey.set(key, {
      name: row.schoolName?.trim() ? row.schoolName.trim() : null,
      code: row.schoolIdentifier?.trim() ? row.schoolIdentifier.trim() : null,
      rowCount: 1,
    });
  }
  return [...byKey].map(([key, value]) => ({ key, ...value }));
}

/**
 * What the file said, reduced to something comparable. Two rows spelling one school
 * differently still group apart here, and are then matched to the same organization —
 * which is the honest order: the file said two things, and both resolve to one school.
 *
 * The name comes first and the code last, which is the whole reason this reads oddly. A
 * normalised name holds nothing but letters, digits and single spaces, so the first bar is
 * always the boundary between the two; a code is only trimmed and upper-cased, so it may
 * contain a bar itself and has to be the part that runs to the end. Put the other way
 * round, a code ending in a bar could forge the boundary and quietly merge two schools.
 *
 * This key also travels to the browser and back as the name of a field, so it is kept to
 * ordinary printable text.
 */
export function suppliedSchoolKey(name: string | null, code: string | null): string {
  const normalizedCode = code?.trim() ? normalizeOrganizationCode(code) : "";
  const normalizedName = name?.trim() ? normalizeOrganizationName(name) : "";
  return `${normalizedName}|${normalizedCode}`;
}

/** The key a row with no school at all falls under. */
export const NO_SCHOOL_KEY = suppliedSchoolKey(null, null);

export function matchSuppliedSchools(
  supplied: readonly SuppliedSchool[],
  candidates: readonly OrganizationCandidate[],
): readonly SchoolMatchResult[] {
  return supplied.map((school) => ({
    ...school,
    match: school.name === null && school.code === null
      ? { state: "unmatched" as const }
      : matchOrganization(candidates, { name: school.name, code: school.code }),
  }));
}

/**
 * Whether the import may proceed without somebody looking at it first.
 *
 * An unmatched school is fine: those rows simply belong to no organization, which is
 * honest and correctable afterwards. An **ambiguous** one is not, because choosing between
 * two schools with the same name is the one mistake here that shows a school another
 * school's teachers and their addresses. That decision belongs to a person.
 */
export function needsConfirmation(matches: readonly SchoolMatchResult[]): boolean {
  return matches.some((school) => school.match.state === "ambiguous");
}

/**
 * The organization each row belongs to, after an organizer has answered anything doubtful.
 *
 * An answer always wins over a proposal, including an answer of "no school" — an organizer
 * who clears a match meant to clear it, and a confident match must not quietly override
 * them.
 */
export function resolveRowOrganizations(
  rows: readonly { schoolName: string | null; schoolIdentifier: string | null }[],
  matches: readonly SchoolMatchResult[],
  answers: Readonly<Record<string, string | null>> = {},
): readonly (string | null)[] {
  const proposed = new Map<string, string | null>();
  for (const school of matches) {
    proposed.set(school.key, school.match.state === "matched" ? school.match.id : null);
  }
  return rows.map((row) => {
    const key = suppliedSchoolKey(row.schoolName, row.schoolIdentifier);
    return Object.hasOwn(answers, key) ? answers[key] ?? null : proposed.get(key) ?? null;
  });
}
